/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useEffect } from 'react';
import { bool, func, shape, string } from 'prop-types';

// Utils
import { useGetAffiliateOptions } from 'hooks';
// eslint-disable-next-line import/order
import { stateList } from '../../../utils/const';

// Components
import { Box, Button, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ChartSection from './chart-section/ChartSection.connector';
import ChartSectionSkeleton from './chart-section/ChartSectionSkeleton';
import FilterDrawer from './filter-drawer/FilterDrawer.connector';
import ListTabs from './list-tabs/ListTabs';
import HistoryPanel from './history-panel/HistoryPanel.connector';
import TimeSection from './time-section/TimeSection.connector';

const MainPanel = props => {
  const {
    activeLocation: { locationId, nameShort, address },
    activeTab,
    bolDate,
    fetchBolHistory,
    fetchLocationDetails,
    fetchLocationLoadTimes,
    locationDetailsPending,
    locationLoadTimesPending,
    locationsPending,
    selectedAffiliate,
    setActiveTab,
    setMobileDrawerOpen,
    setMobileFilterDrawerOpen,
  } = props;
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  const tabs = [
    { id: 'waitTimes', name: 'Wait Times', shortName: 'Wait Times' },
    { id: 'history', name: 'History', shortName: 'BOL History' },
  ];

  const affiliates = useGetAffiliateOptions(['supplier']);
  const foundSupplier = affiliates.find(({ value }) => value === selectedAffiliate) || null;

  useEffect(() => {
    if (locationId) {
      fetchLocationDetails({ locationId });
      fetchBolHistory({
        date: bolDate,
        locationId,
        supplier: {
          companyCustomerIdName: foundSupplier?.customerIdName,
          dataCompanyId: foundSupplier?.dataCompanyId,
        },
      });
      fetchLocationLoadTimes({
        locationId,
        supplier: {
          companyCustomerIdName: foundSupplier?.customerIdName,
          dataCompanyId: foundSupplier?.dataCompanyId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, foundSupplier?.customerIdName, foundSupplier?.dataCompanyId, bolDate]);

  const pendingState = locationsPending || locationDetailsPending || locationLoadTimesPending;

  const renderHeaderInfo = () => {
    if (locationsPending) {
      return (
        <>
          <Box height={28} sx={{ xs: 'none', md: 'revert' }} width="25%">
            <Skeleton height="100%" variant="rectangular" width="100%" />
          </Box>
          <Box height={18} mb={1} mt={0.5} width="50%">
            <Skeleton height="100%" variant="rectangular" width="100%" />
          </Box>
        </>
      );
    }
    return (
      <>
        <Typography sx={{ xs: 'none', md: 'revert' }} variant="h5">
          {nameShort}
        </Typography>
        <Typography sx={{ mb: 1 }} variant="body2">
          {address &&
            `${address.street}, ${address.city}, ${stateList[address.state]} ${address.zipCode}`}
        </Typography>
      </>
    );
  };

  return (
    <Box
      sx={{
        height: { xs: '100%', md: 'revert' },
        marginTop: { xs: 'revert', md: 6 },
        // Allows desktop view to pad correctly with expanded sidebar
        paddingLeft: { xs: 'revert', md: 52.5 },
      }}
    >
      <Box
        sx={{
          height: { xs: '100%', lg: 'revert' },
          maxWidth: 1000,
          p: { xs: 'revert', md: 3 },
        }}
      >
        <Box
          sx={{
            borderBottom: ({ palette }) => ({
              xs: `1px solid ${palette.graphic.light}`,
              md: 'none',
            }),
            pb: { xs: 1.5, lg: 1 },
            pt: { xs: 2, lg: 'revert' },
            px: { xs: 2, lg: 'revert' },
          }}
        >
          <Box justifyContent="space-between" sx={{ display: { xs: 'flex', md: 'none' } }}>
            <Button
              onClick={() => setMobileDrawerOpen(false)}
              sx={{
                ...theme.typography.label,
                color: 'secondary.main',
                justifyContent: 'flex-start',
                // negative left margin is so the button actually lines up with content
                ml: -1,
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                mr: { xs: 0, sm: 3 },
                mt: 0.5,
                pb: 1,
                pt: 0,
                px: 0,
                textTransform: 'capitalize',
              }}
              variant="text"
            >
              <ChevronLeftRounded />
              Locations
            </Button>
            <Button
              onClick={() => {
                setMobileFilterDrawerOpen(true);
              }}
              sx={{
                ...theme.typography.label,
                color: 'secondary.main',
                display: activeTab === 'history' ? 'inline-flex' : 'none',
                justifyContent: 'flex-end',
                // negative left margin is so the button actually lines up with content
                ml: -1,
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                mr: { xs: 0, sm: 3 },
                mt: 0.5,
                pb: 1,
                pt: 0,
                px: 0,
                textTransform: 'capitalize',
              }}
              variant="text"
            >
              Filters
              <ChevronRightRounded />
            </Button>
          </Box>
          {renderHeaderInfo()}
          <ListTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            pendingState={locationsPending}
            tabs={tabs}
          />
        </Box>
        <TabContext value={activeTab}>
          <TabPanel sx={{ p: 0 }} value="waitTimes">
            <TimeSection pendingState={pendingState} />
            {pendingState ? <ChartSectionSkeleton /> : <ChartSection />}
          </TabPanel>
          <TabPanel sx={{ p: 0 }} value="history">
            <HistoryPanel />
            {!mdUp && <FilterDrawer />}
          </TabPanel>
        </TabContext>
      </Box>
    </Box>
  );
};

MainPanel.propTypes = {
  activeLocation: shape({}).isRequired,
  activeTab: string.isRequired,
  bolDate: string.isRequired,
  fetchBolHistory: func.isRequired,
  fetchLocationDetails: func.isRequired,
  fetchLocationLoadTimes: func.isRequired,
  locationDetailsPending: bool.isRequired,
  locationLoadTimesPending: bool.isRequired,
  locationsPending: bool.isRequired,
  selectedAffiliate: string.isRequired,
  setActiveTab: func.isRequired,
  setMobileDrawerOpen: func.isRequired,
  setMobileFilterDrawerOpen: func.isRequired,
};

export default MainPanel;
